import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { RejectType, useRouter } from "@uirouter/react";
import { swSettings } from "app/common/services/swSettings";

export enum EPageStatus {
    SUCCESS,
    ERROR,
    NOTFOUND,
}

interface IWebsiteAnalysisContext {
    loading: boolean;
    pageStatus: EPageStatus;
}

const context = React.createContext<IWebsiteAnalysisContext>(null);
export const useWebsiteAnalysisContext = () => {
    return useContext(context);
};

export const WebsiteAnalysisProvider = ({ children }) => {
    const router = useRouter();
    const [loading, setLoading] = useState<boolean>(false);
    const onStartHandler = useRef<Function>(null);
    const onSuccessHandler = useRef<Function>(null);
    const onErrorHandler = useRef<Function>(null);
    const wasUnmounted = useRef<boolean>(false);
    const [pageStatus, setPageStatus] = useState<EPageStatus>(null);

    // useEffect without any dependency, Since this component is rendered once, unless you left website analysis pages
    useEffect(() => {
        // set up navigation events 3 handlers:
        // 1) on start navigation
        // 2) on finish navigation
        // 3) on page error
        onStartHandler.current = router.transitionService.onStart({}, (transition) => {
            setLoading(true);
        });
        onSuccessHandler.current = router.transitionService.onSuccess({}, (transition) => {
            setPageStatus(EPageStatus.SUCCESS);
            // im not sure why timeout is needed here, This code was copied from app.ts
            setTimeout(() => {
                // if the component was unmounted, Skip state change
                if (!wasUnmounted.current) {
                    setLoading(false);
                }
            }, 400);
        });
        onErrorHandler.current = router.transitionService.onError({}, (transition) => {
            const error = transition.error();
            if (error.type === RejectType.IGNORED) {
                return;
            }
            if (error.type === RejectType.INVALID || error.type === RejectType.ERROR) {
                const errorCode = error.detail;
                if (errorCode === 406) {
                    location.href = swSettings.current.resources.defaulturl;
                }
                if (errorCode === 404) {
                    setPageStatus(EPageStatus.NOTFOUND);
                }
            }
            setLoading(false);
        });
        // clean up
        return () => {
            wasUnmounted.current = true;
            if (typeof onStartHandler.current === "function") {
                onStartHandler.current();
            }
            if (typeof onSuccessHandler.current === "function") {
                onSuccessHandler.current();
            }
            if (typeof onErrorHandler.current === "function") {
                onErrorHandler.current();
            }
        };
    }, []);
    const contextValue = useMemo<IWebsiteAnalysisContext>(() => {
        return {
            loading,
            pageStatus,
        };
    }, [loading, pageStatus]);
    return <context.Provider value={contextValue}>{children}</context.Provider>;
};
